import { IconArrowRight, IconBooks, IconMapPin, IconPhone, IconSpeakerphone, IconUser } from '@tabler/icons-react';
import { useTranslation } from '../../../../i18n/hooks/useTranslation';
import { PosterLogo, PosterSheet, fs, hexToRgba, numberScale, posterFields, solid, valueFitScale } from '../posterParts';

/**
 * "Neon" — a dark, modern poster: glowing colour blobs and a faint grid on near-black, the vacancy count inside a glowing ring,
 * frosted "glass" cards for the qualification and age, the two dates on a timeline bar and a gradient call-to-action.
 * Palette: base, glowA, glowB, glowC, accent, accent2, ctaA, ctaB.
 */
const FONT = "'Anek Bangla', 'Noto Sans Bengali', sans-serif";
const GLASS = { background: 'rgba(255,255,255,0.07)', border: '0.25cqw solid rgba(255,255,255,0.2)', boxShadow: '0 1cqw 2.4cqw rgba(0,0,0,0.35)' };

function GlassCard({ p, Icon, label, children }) {
    return (
        <div style={{ ...GLASS, borderRadius: fs(3.4), padding: `${fs(2.2)} ${fs(2.6)}` }}>
            <div className="flex items-center" style={{ gap: fs(1.6), marginBottom: fs(1.2) }}>
                <div className="flex flex-none items-center justify-center rounded-full" style={{ width: fs(6.4), height: fs(6.4), background: hexToRgba(p.accent, 0.18), border: `0.25cqw solid ${hexToRgba(p.accent, 0.7)}` }}>
                    <Icon style={{ width: fs(3.8), height: fs(3.8), color: p.accent }} stroke={2.2} />
                </div>
                <div style={{ color: hexToRgba('#ffffff', 0.72), fontSize: fs(2.7), fontWeight: 700, lineHeight: 1.15 }}>{label}</div>
            </div>
            {children}
        </div>
    );
}

function DateNode({ p, label, value, align }) {
    return (
        <div className="flex flex-none flex-col" style={{ alignItems: align }}>
            <div style={{ color: hexToRgba('#ffffff', 0.7), fontSize: fs(2.7), fontWeight: 700, lineHeight: 1.1 }}>{label}</div>
            <div style={{ color: '#fff', fontSize: fs(4.4), fontWeight: 800, lineHeight: 1.2, marginTop: fs(0.4), textShadow: `0 0 1.4cqw ${hexToRgba(p.accent, 0.55)}` }}>{value}</div>
        </div>
    );
}

export default function NeonDesign({ job, business, theme, showFooter = true, className = '' }) {
    const { t } = useTranslation();
    const p = theme.palette;
    const f = posterFields(job, business, t);
    const eduScale = valueFitScale(f.edu, 26);
    const ageScale = valueFitScale(f.age, 20);
    const gradId = `neon-ring-${theme.id}`;

    const body = (
        <div className="flex flex-1 flex-col justify-center" style={{ padding: `${fs(5.5)} 6.5cqw ${fs(3)}`, gap: fs(3) }}>
            <div className="flex items-center justify-between">
                <div style={{ ...GLASS, borderRadius: fs(4), padding: fs(1.4), boxShadow: `0 0 3cqw ${hexToRgba(p.accent, 0.35)}` }}>
                    <PosterLogo job={job} size={13} style={{ background: '#fff', borderRadius: fs(3), padding: fs(1) }} />
                </div>
                <div className="flex items-center" style={{ border: `0.3cqw solid ${p.accent}`, color: p.accent, borderRadius: '99px', padding: `${fs(1)} ${fs(3)}`, gap: fs(1.4), fontSize: fs(3.1), fontWeight: 800, boxShadow: `0 0 2cqw ${hexToRgba(p.accent, 0.4)}, inset 0 0 1.4cqw ${hexToRgba(p.accent, 0.2)}` }}>
                    <IconSpeakerphone style={{ width: fs(3.8), height: fs(3.8) }} stroke={2.2} />
                    <div>{f.label.notice}</div>
                </div>
            </div>

            <div>
                <div style={{ color: '#fff', fontSize: fs(7), fontWeight: 800, lineHeight: 1.13, textShadow: `0 0 3cqw ${hexToRgba(p.glowA, 0.6)}` }}>{f.org}</div>
                {f.position && <div style={{ color: p.accent, fontSize: fs(3.7), fontWeight: 700, lineHeight: 1.25, marginTop: fs(1.4) }}>{f.position}</div>}
            </div>

            <div className="flex items-center" style={{ gap: fs(3) }}>
                {/* the glowing ring */}
                <div className="relative flex-none" style={{ width: fs(40), height: fs(40), filter: `drop-shadow(0 0 1.6cqw ${hexToRgba(p.accent, 0.55)})` }}>
                    <svg viewBox="0 0 100 100" width="100%" height="100%" aria-hidden="true">
                        <defs>
                            <linearGradient id={gradId} x1="0" y1="0" x2="1" y2="1">
                                <stop offset="0" stopColor={p.accent} />
                                <stop offset="0.55" stopColor={p.glowA} />
                                <stop offset="1" stopColor={p.glowC} />
                            </linearGradient>
                        </defs>
                        <circle cx="50" cy="50" r="46" fill="rgba(255,255,255,0.05)" stroke={`url(#${gradId})`} strokeWidth="3.2" />
                        <circle cx="50" cy="50" r="39.5" fill="none" stroke="rgba(255,255,255,0.25)" strokeWidth="0.6" strokeDasharray="1.6 2.4" />
                    </svg>
                    <div className="absolute inset-0 flex flex-col items-center justify-center text-center">
                        <div style={{ color: hexToRgba('#ffffff', 0.8), fontSize: fs(3), fontWeight: 700, lineHeight: 1 }}>{f.label.vacancy}</div>
                        <div style={{ color: '#fff', fontSize: `calc(${14 * numberScale(f.vacancy)}cqw * var(--fs, 1))`, fontWeight: 900, lineHeight: 1.02, textShadow: `0 0 2cqw ${hexToRgba(p.accent, 0.7)}` }}>{f.vacancy}</div>
                        <div style={{ color: p.accent, fontSize: fs(3.6), fontWeight: 800, lineHeight: 1 }}>{f.label.unit}</div>
                    </div>
                </div>

                <div className="flex min-w-0 flex-1 flex-col" style={{ gap: fs(2.4) }}>
                    <GlassCard p={p} Icon={IconBooks} label={f.label.edu}>
                        <div style={{ color: '#fff', fontSize: `calc(${3.5 * eduScale}cqw * var(--fs, 1))`, fontWeight: 800, lineHeight: 1.22 }}>{f.edu}</div>
                    </GlassCard>
                    <GlassCard p={p} Icon={IconUser} label={f.label.age}>
                        <div style={{ color: '#fff', fontSize: `calc(${3.5 * ageScale}cqw * var(--fs, 1))`, fontWeight: 800, lineHeight: 1.22 }}>{f.age}</div>
                    </GlassCard>
                </div>
            </div>

            {/* the dates on a timeline */}
            <div style={{ ...GLASS, borderRadius: fs(3.4), padding: `${fs(2.4)} ${fs(3.4)}` }}>
                <div className="flex items-center" style={{ gap: fs(2.4) }}>
                    <DateNode p={p} label={f.label.start} value={f.start} align="flex-start" />
                    <div className="relative flex-1" style={{ height: fs(1) }}>
                        <div className="absolute inset-x-0 top-1/2 -translate-y-1/2" style={{ height: '0.55cqw', borderRadius: '99px', background: `linear-gradient(90deg, ${p.accent}, ${p.glowC})`, boxShadow: `0 0 1.6cqw ${hexToRgba(p.accent, 0.6)}` }} />
                        <div className="absolute left-0 top-1/2 -translate-y-1/2 rounded-full" style={{ width: fs(2.6), height: fs(2.6), background: p.accent, boxShadow: `0 0 1.6cqw ${p.accent}` }} />
                        <div className="absolute right-0 top-1/2 -translate-y-1/2 rounded-full" style={{ width: fs(2.6), height: fs(2.6), background: p.glowC, boxShadow: `0 0 1.6cqw ${p.glowC}` }} />
                    </div>
                    <DateNode p={p} label={f.label.end} value={f.end} align="flex-end" />
                </div>
            </div>

            <div style={{ color: hexToRgba('#ffffff', 0.66), fontSize: fs(2.6), fontWeight: 600, textAlign: 'center' }}>✓ {f.label.promo}</div>

            <div className="flex items-center justify-center" style={{ background: `linear-gradient(90deg, ${p.ctaA}, ${p.ctaB})`, borderRadius: '99px', padding: `${fs(2.2)} ${fs(3)}`, gap: fs(2.2), color: '#fff', boxShadow: `0 0 3.2cqw ${hexToRgba(p.ctaA, 0.55)}, 0 1cqw 2cqw rgba(0,0,0,0.4)` }}>
                <div style={{ fontSize: fs(4.6), fontWeight: 900 }}>{f.label.cta}</div>
                <IconArrowRight style={{ width: fs(5.4), height: fs(5.4) }} stroke={3} />
            </div>
        </div>
    );

    const footer = showFooter ? (
        <div className="text-center" style={{ margin: `0 6.5cqw ${fs(4.4)}`, ...GLASS, borderRadius: fs(3.6), padding: `${fs(2.4)} ${fs(3)}` }}>
            <div className="flex items-center justify-center" style={{ gap: fs(2.2) }}>
                {f.shop.logo && <img src={f.shop.logo} alt="" className="flex-none rounded-full object-contain" style={{ width: fs(8.4), height: fs(8.4), background: '#fff', padding: '0.6cqw' }} />}
                <div style={{ color: p.accent, fontSize: fs(5.8), fontWeight: 900, lineHeight: 1.12, opacity: f.shop.placeholder ? 0.65 : 1, textShadow: `0 0 1.6cqw ${hexToRgba(p.accent, 0.5)}` }}>{f.shop.nameShown}</div>
            </div>
            <div className="flex items-center justify-center" style={{ gap: fs(1.2), marginTop: fs(0.8), color: '#fff', fontSize: fs(3.5), lineHeight: 1.25, opacity: f.shop.addressPlaceholder ? 0.6 : 0.9 }}>
                <IconMapPin className="flex-none" style={{ width: fs(3.8), height: fs(3.8), color: p.accent }} />
                <div>{f.shop.addressShown}</div>
            </div>
            {f.shop.phone && (
                <div className="flex items-center justify-center" style={{ gap: fs(1.2), marginTop: fs(0.6), color: '#fff', fontSize: fs(4.5), fontWeight: 800 }}>
                    <IconPhone className="flex-none" style={{ width: fs(4.2), height: fs(4.2), color: p.accent }} />
                    <div>{f.shop.phone}</div>
                </div>
            )}
        </div>
    ) : null;

    const background = [
        `radial-gradient(62% 34% at 8% 4%, ${hexToRgba(p.glowA, 0.55)}, transparent 72%)`,
        `radial-gradient(52% 30% at 100% 40%, ${hexToRgba(p.glowC, 0.3)}, transparent 72%)`,
        `radial-gradient(72% 36% at 68% 104%, ${hexToRgba(p.glowB, 0.42)}, transparent 72%)`,
        'linear-gradient(rgba(255,255,255,0.045) 0.14cqw, transparent 0.14cqw) 0 0 / 6cqw 6cqw',
        'linear-gradient(90deg, rgba(255,255,255,0.045) 0.14cqw, transparent 0.14cqw) 0 0 / 6cqw 6cqw',
        solid(p.base),
    ].join(', ');

    return (
        <PosterSheet
            deps={[job, business, theme, showFooter, t]}
            className={className}
            fontFamily={FONT}
            background={background}
            body={body}
            footer={footer}
        />
    );
}
